import { defineStore } from "pinia";
import { computed, ref } from "vue";

import type { Team } from "@/model/session";
import { useGlobalI18n } from "@/translations/i18n";
import { asReadonly } from "@/utils/vue";

import { NO_ART, useArtStore } from "./art";
import { useUserStore } from "./user";

export const NO_TEAM = { id: "", name: "" };

const { t } = useGlobalI18n();

export const useTeamStore = defineStore(
  "team",
  () => {
    const teams = ref<Team[]>([]);
    const externals = ref<Team[]>([]);
    const current = ref<Team>(NO_TEAM);

    function teamsInArt(artId: string, withExternal = false) {
      const all = withExternal ? [...teams.value, ...externals.value] : teams.value;
      // REN-9638 backward compatibility fix
      return artId === "" ? all : all.filter((team) => team.artId === artId);
    }

    function setCurrent(team: Team) {
      current.value = team;
      useArtStore().setCurrent(useArtStore().artById(team.artId) || NO_ART);
    }

    return {
      teams: asReadonly(teams),
      externals: asReadonly(externals),
      current: asReadonly(current),

      noTeam: computed(() => ({ id: NO_TEAM.id, name: t("general.noTeam") })),
      teamsInCurrentArt: computed(() => {
        const artId = useArtStore().current.id;
        return artId ? teamsInArt(artId) : teams.value;
      }),
      externalsInCurrentArt: computed(() =>
        teamsInArt(useArtStore().current.id, true).filter((team) => team.external),
      ),

      isExternalTeam: (teamId: string) =>
        !!externals.value.find((team: Team) => team.id === teamId),
      setTeams: (teamList: Team[], externalList: Team[]) => {
        teams.value = teamList;
        externals.value = externalList;
        setCurrent(NO_TEAM);
      },
      // The id is the identity. A name is only a fallback for callers without an id:
      // names cached on stickies go stale when a team is renamed.
      findTeam: (query: { id?: string | null; name?: string | null; withExternal?: boolean }) => {
        if (query.id === null || (query.name === null && !query.id)) return;
        if (query.id || query.name) {
          const all = query.withExternal ? [...teams.value, ...externals.value] : teams.value;
          return all.find((t) => (query.id ? query.id === t.id : query.name === t.name));
        }
      },
      teamsInArt,
      setCurrent,
    };
  },
  { autoReset: true },
);

export function isNotUserTeam(team: Team) {
  const userTeams = useUserStore().technicalUser.teams;
  return !userTeams.some((userTeam) => userTeam.id === team.id);
}

export function isUserTeam(team: Team) {
  const userTeams = useUserStore().technicalUser.teams;
  return userTeams.some((userTeam) => userTeam.id === team.id);
}
